/**
 * LEDGER01: the case canvas's view of the transfer ledger (`GET /cases/{id}/transfers`).
 *
 * The node inspector says, for a wallet, "Transfers in ledger: n (k not on canvas)". n is the endpoint's `total` for the
 * wallet; k is the number of those transfers whose other side is not a node on the canvas. Both come from the endpoint
 * (`total`, `counterparties`); nothing is recomputed from the rows. Pure helpers, no fetch.
 */

export interface LedgerCounterparty { address: string; transfers: number }

/** Comparison form of an address: EVM addresses compare without case, every other chain's as written (R1). */
export function addressKey(address: unknown): string {
  const s = typeof address === 'string' ? address.trim() : '';
  return /^0x/i.test(s) ? s.toLowerCase() : s;
}

/** The set of address keys on the canvas (a node's `address`, else its `id` with a chain prefix removed). */
export function canvasAddressKeys(nodes: Array<Record<string, unknown>>): Set<string> {
  const out = new Set<string>();
  for (const d of nodes) {
    const raw = typeof d.address === 'string' && d.address ? d.address : (typeof d.id === 'string' ? d.id : '');
    if (!raw) continue;
    out.add(addressKey(raw));
    const bare = raw.replace(/^(?:ent:wallet:|[A-Za-z]{2,10}:)/, '');
    if (bare && bare !== raw) out.add(addressKey(bare));
  }
  return out;
}

/** Transfers whose other side is not on the canvas: the sum of `transfers` over the counterparties not in `onCanvas`. */
export function transfersNotOnCanvas(counterparties: LedgerCounterparty[] | undefined | null, onCanvas: Set<string>): number {
  let k = 0;
  for (const c of counterparties || []) {
    if (!c || typeof c.address !== 'string') continue;
    if (!onCanvas.has(addressKey(c.address))) k += Number(c.transfers) || 0;
  }
  return k;
}

/** "Transfers in ledger: 120 (45 not on canvas)"; the parenthesis is left out when every transfer reaches a canvas node. */
export function ledgerLine(total: number, notOnCanvas: number): string {
  const n = Number.isFinite(total) ? total : 0;
  const k = Number.isFinite(notOnCanvas) ? notOnCanvas : 0;
  return `Transfers in ledger: ${n.toLocaleString('en-US')}${k > 0 ? ` (${k.toLocaleString('en-US')} not on canvas)` : ''}`;
}

/** The endpoint path for one wallet: `limit=1` for the count, a larger limit to list rows. */
export function ledgerPath(caseId: string, wallet: string, chain: string | undefined, limit: number, offset = 0): string {
  const q = new URLSearchParams({ wallet, limit: String(limit), offset: String(offset) });
  if (chain) q.set('chain', chain.toUpperCase());
  return `/cases/${encodeURIComponent(caseId)}/transfers?${q.toString()}`;
}
